import { useEffect, useRef } from 'react';
import { Button } from '@/components/Button';

export interface AppErrorFallbackProps {
  error: Error | null;
  /** Clears the boundary and re-renders the current route. */
  onRetry: () => void;
  /** Leaves the failed route for a known-good one. */
  onGoHome: () => void;
}

/**
 * Full-page fallback shown when a render error has unmounted the routed
 * application.
 *
 * It renders its own landmarks deliberately: by the time this is on screen
 * `MainLayout` has been unmounted along with the rest of the tree, so there is
 * no competing `<main>` to duplicate.
 *
 * MUST STAY CONTEXT-FREE. This is also rendered by `TopLevelErrorBoundary`,
 * which sits above the Router and every provider. Importing a Router, Theme or
 * Auth hook here would throw inside the fallback itself, and a fallback that
 * throws gives the blank page back. `Button` is context-free; keep it that way.
 */
export function AppErrorFallback({ error, onRetry, onGoHome }: AppErrorFallbackProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Screen readers are inconsistent about announcing a live region that
    // mounts already populated. Moving focus is the reliable path, and it puts
    // keyboard users on the message rather than at the top of the document.
    headingRef.current?.focus();
  }, []);

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-between"
      data-testid="app-error-boundary"
    >
      <header className="py-4 px-4 sm:px-6 lg:px-8 flex-shrink-0" role="banner">
        <div className="max-w-7xl mx-auto flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z" />
            </svg>
          </div>
          <span className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-heading">
            SupportDesk
          </span>
        </div>
      </header>

      <main
        id="main-content"
        className="flex-1 flex flex-col justify-center py-6 px-4 sm:px-6 lg:px-8"
        role="main"
      >
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="mx-auto h-20 w-20 text-error-500 mb-6">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>

            {/* The announced region is the message only. A live region should
                not contain the interactive controls, and `role="alert"` already
                implies an assertive live region - `aria-live` as well is
                redundant. */}
            <div role="alert">
              {/* No `focus:outline-none` on the heading. It is only ever focused
                  programmatically - tabIndex -1 keeps it out of the tab order -
                  so showing where focus landed is the point, not noise. */}
              <h1
                ref={headingRef}
                tabIndex={-1}
                className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3"
                data-testid="app-error-boundary-title"
              >
                Something went wrong
              </h1>

              <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto text-sm">
                This page hit an unexpected error and could not be displayed. Other pages
                should still work. Try again, or head back to your dashboard.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button
                variant="primary"
                onClick={onRetry}
                data-testid="app-error-boundary-retry"
              >
                Try again
              </Button>
              <Button
                variant="outline"
                onClick={onGoHome}
                data-testid="app-error-boundary-home"
              >
                Go to dashboard
              </Button>
            </div>

            {error?.message && (
              <div className="mt-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Technical detail
                </p>
                <p
                  className="text-xs font-mono text-error-600 dark:text-error-400 break-words"
                  data-testid="app-error-boundary-detail"
                >
                  {error.message}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Quote this when reporting the problem. Full details are in the browser
                  console.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="py-4 text-center text-xs text-gray-500 dark:text-gray-400 flex-shrink-0" role="contentinfo">
        <p>SupportDesk MVP - Professional Support Desk Solution</p>
      </footer>
    </div>
  );
}

export default AppErrorFallback;
